"use client";
import { useBottomNavigation } from "@/components/complex/bottom-navigation";
import { useHeader } from "@/components/complex/header";
import Button from "@/components/primitives/button";
import Typography from "@/components/primitives/typography";
import useLocation from "@/libs/platform/location/use-location";
import { MapPin } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

import api from "@/libs/api";
import FeedItems from "@/components/complex/feed-items";
import styles from "./styles.module.scss";
import { useAsyncEffect } from '@/hooks/use-async-effect';

// 위치 권한 상태 타입은 공용 훅에서 가져옵니다.
// type LocationPermissionStatus = 'checking' | 'granted' | 'denied' | 'unavailable' | 'prompt';

const NearbyScreen = () => {
	useHeader({
		useBack: false,
		title: "주변",
	});

	useBottomNavigation(true);
	const router = useRouter();
	const [showPermissionRequest, setShowPermissionRequest] = useState(true);
	const [address, setAddress] = useState<string>("");

	// 공용 위치 훅 사용
	const {
		permissionStatus,
		position: userLocation,
		isLoading,
		getCurrentPosition,
		requestPermission: requestLocationPermission,
		stopWatch,
	} = useLocation();

	// 페이지 로드 시 위치 권한 확인 및 권한 변경 감지
	useEffect(() => {
		// 마운트 시 위치 권한이 있다면 자동으로 가져오기 시도
		if (permissionStatus === "granted" && !userLocation && !isLoading) {
			getCurrentPosition();
		}

		return () => {
			// 언마운트 시 워치 정리 (혹시 켜졌다면)
			stopWatch().catch(() => {});
		};
	}, [
		stopWatch,
		permissionStatus,
		userLocation,
		isLoading,
		getCurrentPosition,
	]);

	// 위치 정보를 기반으로 주소 가져오기
	useAsyncEffect(async () => {
    if (userLocation) {
      try {
        const res = await api.maps.reverseGeocoding(
          userLocation.coords.latitude,
          userLocation.coords.longitude
        );
        if (res.status === 'ok' && res.results.length > 0) {
          const region = res.results[0].region;
          setAddress(
            [region.area1, region.area2, region.area3]
              .filter((a) => !!a)
              .join(' ')
          );
        }
      } catch (err: any) {
        console.error('역 지오코딩 실패:', err);
      }
    }
  }, [userLocation]);

	// 위치 권한이 없는 경우 표시할 UI
	if (permissionStatus === "checking") {
		return (
			<div className={styles.root}>
				<Typography>위치 권한을 확인하는 중이에요...</Typography>
			</div>
		);
	}

	// 위치 정보를 가져오는 중인 경우 표시할 UI
	if (isLoading) {
		return (
			<div className={styles.root}>
				<Typography>위치를 찾는 중이에요...</Typography>
			</div>
		);
	}

	if (
		showPermissionRequest &&
		(permissionStatus === "denied" ||
			permissionStatus === "unavailable" ||
			permissionStatus === "prompt")
	) {
		const isDenied =
			permissionStatus === "denied" || permissionStatus === "unavailable";

		const handleRequestPermission = async () => {
			if (isDenied) {
				alert("브라우저 설정에서 위치 권한을 허용해주세요.");
			} else {
				await requestLocationPermission();
				await getCurrentPosition();
			}
		};

		return (
			<div className={`${styles.root} ${styles.permission}`}>
				<MapPin
					width={48}
					height={48}
					strokeWidth={1.25}
					className={styles.icon}
				/>
				<div className={styles.textContainer}>
					<Typography size="large" weight="bold" align="center">
						위치 권한이 필요해요
					</Typography>
					<Typography align="center" lineHeight={1.2}>
						주변 장소와 관련된 피드를 표시하기 위해
						<br />
						위치 정보가 필요해요
					</Typography>
				</div>
				<div className={styles.buttonContainer}>
					<Button
						onClick={handleRequestPermission}
						className={styles.permissionButton}
					>
						{isDenied ? '설정에서 위치 허용하기' : '위치 권한 허용하기'}
					</Button>
				</div>
			</div>
		);
	}
	// 위치 권한이 있는 경우 피드 표시
	return (
    <>
      <div className={`${styles.root} ${userLocation ? styles.results : ''}`}>
        {permissionStatus === 'granted' && (
          <div className={styles.infoBar}>
            <MapPin width={24} height={24} className={styles.infoBarIcon} />
            <div className={styles.infoBarText}>
              {address && (
                <Typography weight="bold" lineHeight={1.4}>{address}</Typography>
              )}
              <p className={styles.infoBarDescription}>
                주변의 장소와 연관된 피드를 보여드릴게요
              </p>
            </div>
          </div>
        )}
        {userLocation ? (
          <div className={styles.feedColumn}>
            <FeedItems
              className={styles.feedGrid}
              location={{
                lat: userLocation.coords.latitude,
                lon: userLocation.coords.longitude,
              }}
              distance={'10km'}
              onEmptyResult={() => (
                <div className={styles.empty}>
                  <Typography align="center" size="small">
                    주변 10km 이내에 등록된 피드가 없어요
                  </Typography>
                </div>
              )}
            />
          </div>
        ) : (
          <div className={styles.root}>
            {!showPermissionRequest && permissionStatus !== 'granted' && (
              <Typography>
                위치 권한이 없어 주변 피드를 불러올 수 없어요
              </Typography>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default NearbyScreen;
